/**
 * Shifts Service
 * All shift-related database operations using Supabase.
 */

import { supabase } from '../../utils/supabase_config';
import { SupabaseService } from './supabaseService';
import { TABLES } from './tables';

export class ShiftsService extends SupabaseService {
  /**
   * Fetch all shifts (admin view).
   */
  static async getShiftsForAdmin() {
    return this.getAll(TABLES.SHIFTS);
  }

  /**
   * Fetch shifts (admin view) within a start_time_secs range.
   * @param {number} startSecs - epoch ms lower bound (inclusive)
   * @param {number} endSecs   - epoch ms upper bound (exclusive)
   */
  static async getShiftsForDateRange(startSecs, endSecs) {
    return this.getWhere(TABLES.SHIFTS, [
      { field: 'start_time_secs', operator: '>=', value: startSecs },
      { field: 'start_time_secs', operator: '<',  value: endSecs }
    ]);
  }

  /**
   * Fetch shifts for a specific client within a start_time_secs range.
   * @param {string} clientName
   * @param {number} startSecs - epoch ms lower bound (inclusive)
   * @param {number} endSecs   - epoch ms upper bound (exclusive)
   */
  static async getShiftsForClientInRange(clientName, startSecs, endSecs) {
    return this.getWhere(TABLES.SHIFTS, [
      { field: 'client',          operator: '==', value: clientName },
      { field: 'start_time_secs', operator: '>=', value: startSecs },
      { field: 'start_time_secs', operator: '<',  value: endSecs }
    ]);
  }

  /**
   * Fetch shifts belonging to a specific caretaker within a start_time_secs range.
   * @param {string} email
   * @param {number} startSecs - epoch ms lower bound (inclusive)
   * @param {number} endSecs   - epoch ms upper bound (exclusive)
   */
  static async getShiftsForCaretakerInRange(email, startSecs, endSecs) {
    return this.getWhere(TABLES.SHIFTS, [
      { field: 'caretaker_email', operator: '==', value: email },
      { field: 'start_time_secs', operator: '>=', value: startSecs },
      { field: 'start_time_secs', operator: '<',  value: endSecs }
    ]);
  }

  /**
   * Fetch only the shifts belonging to a specific caretaker (non-admin view).
   * @param {string} email - Caretaker's email
   */
  static async getShiftsForCaretaker(email) {
    return this.getWhere(TABLES.SHIFTS, [
      { field: 'caretaker_email', operator: '==', value: email }
    ]);
  }

  /**
   * Batch insert multiple shifts atomically using the PostgreSQL RPC.
   * Replaces Firebase writeBatch used in addShift().
   * @param {Array} shiftsArray - Array of shift objects (camelCase or snake_case)
   */
  static async addShiftsBatch(shiftsArray) {
    return this.rpc('insert_shifts_batch', { shifts_json: shiftsArray });
  }

  /**
   * Fetch all future repeating shifts (start_time_secs >= now).
   * Returns raw rows — caller groups by repeating_shift_id.
   */
  static async getFutureRepeatingShifts() {
    try {
      const { data, error } = await supabase
        .from(TABLES.SHIFTS)
        .select('*')
        .eq('repeating_shift', true)
        .gte('start_time_secs', Date.now())
        .order('start_time_secs', { ascending: true });
      if (error) throw error;
      return (data || []).map(row => this._addDocId(row));
    } catch (error) {
      this.handleError('Error fetching repeating shifts', error);
      throw error;
    }
  }

  /**
   * Update scalar fields (client, caretaker, caretaker_email, color) on all
   * future instances of a repeating shift on or after fromSecs.
   */
  static async updateFutureRepeatingShiftFields(repeatingShiftId, fromSecs, updates) {
    try {
      const { error } = await supabase
        .from(TABLES.SHIFTS)
        .update(updates)
        .eq('repeating_shift_id', repeatingShiftId)
        .gte('start_time_secs', fromSecs);
      if (error) throw error;
    } catch (error) {
      this.handleError('Error updating repeating shift fields', error);
      throw error;
    }
  }

  /**
   * Update start/end times on all future instances of a repeating shift.
   * Each shift keeps its own date; only the time component is replaced.
   * @param {string} repeatingShiftId
   * @param {number} fromSecs - epoch ms lower bound (inclusive)
   * @param {string} newStartTime - "HH:MM"
   * @param {string} newEndTime   - "HH:MM"
   */
  static async updateFutureRepeatingShiftTimes(repeatingShiftId, fromSecs, newStartTime, newEndTime) {
    try {
      const { data, error } = await supabase
        .from(TABLES.SHIFTS)
        .select('id, start_time')
        .eq('repeating_shift_id', repeatingShiftId)
        .gte('start_time_secs', fromSecs);
      if (error) throw error;

      await Promise.all((data || []).map(shift => {
        const dateStr = shift.start_time.substring(0, 10); // "YYYY-MM-DD"
        const newStart = `${dateStr}T${newStartTime}:00`;
        const newEnd = `${dateStr}T${newEndTime}:00`;
        return supabase
          .from(TABLES.SHIFTS)
          .update({
            start_time: newStart,
            end_time: newEnd,
            start_time_secs: new Date(newStart).getTime(),
          })
          .eq('id', shift.id);
      }));
    } catch (error) {
      this.handleError('Error updating repeating shift times', error);
      throw error;
    }
  }

  /**
   * Delete all future instances of a repeating shift using a PostgreSQL RPC.
   * Replaces the Firebase multi-delete loop in deleteFutureShifts().
   * @param {string} repeatingShiftId
   * @param {number} startTimeSecs - epoch ms; deletes this shift and all after
   */
  static async deleteFutureShifts(repeatingShiftId, startTimeSecs) {
    return this.rpc('delete_future_repeating_shifts', {
      p_repeating_shift_id: repeatingShiftId,
      p_start_time_secs: startTimeSecs
    });
  }

  /**
   * Update edited start/end times on a shift (caretaker time-edit flow).
   * @param {string} shiftId
   * @param {Object} updates - { edited_start_time, edited_end_time, pretty_edited_start_time, pretty_edited_end_time }
   */
  static async editTimeWorked(shiftId, updates) {
    return this.update(TABLES.SHIFTS, shiftId, updates);
  }

  /**
   * Update all editable fields on a shift (admin edit-shift flow).
   * Replaces the 8-10 sequential updateDoc calls in editInfoForShift().
   * @param {string} shiftId
   * @param {Object} updates
   */
  static async editShiftInfo(shiftId, updates) {
    return this.update(TABLES.SHIFTS, shiftId, updates);
  }

  /**
   * Delete a single shift.
   * @param {string} shiftId
   */
  static async deleteShift(shiftId) {
    return this.delete(TABLES.SHIFTS, shiftId);
  }

  /**
   * Find a shift by caretaker email and a start_time prefix.
   * Used by editTimeWorked to locate the correct shift from calendar data.
   * @param {string} caretakerEmail
   * @param {string} startTimePrefix - e.g. "2024-01-15"
   */
  static async findShiftByStartPrefix(caretakerEmail, clientName, startTimePrefix) {
    try {
      const { data, error } = await supabase
        .from(TABLES.SHIFTS)
        .select('*')
        .eq('caretaker_email', caretakerEmail)
        .eq('client', clientName)
        .like('start_time', `${startTimePrefix}%`);
      if (error) throw error;
      return (data || []).map(row => this._addDocId(row));
    } catch (error) {
      this.handleError('Error finding shift by start prefix', error);
      throw error;
    }
  }
}
